import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { Star } from 'lucide-react';
import { ICompanyDetails, ISimilarCompany } from '../../redux/company/types';
import { formatCurrencyWithAbbrevation } from '../../constants/currency';
import { replaceRouteParam } from '../../utils/routesProcessor';
import { ROUTES } from '../../types/global/routes.types';
import { useDispatch } from 'react-redux';
import { navigate } from '../../redux/navigator/action';
import { LinkBox } from '../LinkBox';
import { generateShareLink } from '../../utils/AppUtils';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { PhotoGrid } from '../WorkProfile/PhotoGrid';
import { COMPANY_BRAND_ORANGE } from '../../constants/companyBrand';

interface ICompanySidebar {
    companyDetails?: ICompanyDetails;
}

const CompanySidebar: React.FC<ICompanySidebar> = ({ companyDetails }) => {
    const { themeStyle } = useThemeContext();
    const { company, leadership, otherPositions, similarCompanies } = companyDetails || {};
    const dispatch = useDispatch();

    const getCompanyInitial = (name: string) => {
        return name.charAt(0).toUpperCase();
    };

    const getRandomColor = (index: number) => {
        const colors = ['#EF4444', '#10B981', '#8B5CF6', '#F59E0B', '#3B82F6', '#EC4899'];
        return colors[index % colors.length];
    };

    const formatRevenue = () => {
        if (!company?.min_revennue && !company?.max_revenue) return 'N/A';

        if (company.min_revennue && company.max_revenue) {
            return `${formatCurrencyWithAbbrevation(company.max_revenue)}`;
        } else if (company.min_revennue) {
            return `${formatCurrencyWithAbbrevation(company.min_revennue)}+`;
        } else {
            return formatCurrencyWithAbbrevation(company.max_revenue!);
        }
    };

    const companyStats = [
        {
            label: 'Founded',
            value: company?.date_of_formation ? new Date(company.date_of_formation).getFullYear().toString() : 'N/A',
        },
        {
            label: 'Employees',
            value:
                company?.min_employees && company?.max_employees
                    ? `${company.min_employees}-${company.max_employees}`
                    : 'N/A',
        },
        {
            label: 'Industry',
            value: company?.service || 'N/A',
        },
        {
            label: 'Revenue',
            value: formatRevenue(),
        },
        {
            label: 'Headquarters',
            value: company?.location?.address || 'N/A',
        },
    ];

    const handleGoToSimilarCompany = (company: ISimilarCompany) => {
        const route = replaceRouteParam(ROUTES.COMPANY, 'id', company.slug);
        dispatch(
            navigate({
                routes: route,
            }),
        );
    };

    const sidebarCardSx = {
        backgroundColor: '#1a1a1a',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 2,
        mb: 2,
        boxShadow: 'none',
    };

    const sectionTitleSx = {
        color: COMPANY_BRAND_ORANGE,
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: '0.6875rem',
        letterSpacing: '0.2em',
        textTransform: 'uppercase' as const,
        mb: 3,
    };

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            {/* Company details */}
            <Card sx={sidebarCardSx}>
                <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Typography component="h2" sx={sectionTitleSx}>
                        Company Details
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        {companyStats.map((stat, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    py: 2,
                                    borderBottom:
                                        index < companyStats.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none',
                                    minWidth: 0,
                                }}>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.45)',
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: '0.6875rem',
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        flexShrink: 0,
                                    }}>
                                    {stat.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        color:
                                            stat.label === 'Revenue' ? COMPANY_BRAND_ORANGE : theme[themeStyle].tag_white,
                                        fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                        fontSize: '0.875rem',
                                        fontWeight: 700,
                                        textAlign: 'right',
                                        wordBreak: 'break-word',
                                        minWidth: 0,
                                    }}>
                                    {stat.value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </CardContent>
            </Card>

            {/* Leadership */}
            {leadership && leadership.data.length > 0 && (
                <Card sx={sidebarCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography component="h2" sx={sectionTitleSx}>
                            Leadership
                        </Typography>
                        <Box sx={{ spaceY: 2 }}>
                            {leadership.data.map((leader, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mb: 1,
                                    }}>
                                    <ThspianAvatar user={leader.user} withMonogram enableDefaultNavigation />
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {`${leader.user.firstName} ${leader.user.lastName}`}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: COMPANY_BRAND_ORANGE,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.6875rem',
                                                letterSpacing: '0.12em',
                                                textTransform: 'uppercase',
                                            }}>
                                            {leader.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Other Positions */}
            {otherPositions && otherPositions.data.length > 0 && (
                <Card sx={sidebarCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography component="h2" sx={sectionTitleSx}>
                            Other Positions
                        </Typography>
                        <Box sx={{ spaceY: 2 }}>
                            {otherPositions.data.map((position, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2,
                                        mb: 1,
                                    }}>
                                    <ThspianAvatar user={position.user} />
                                    <Box>
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.875rem',
                                            }}>
                                            {`${position.user.firstName} ${position.user.lastName}`}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: COMPANY_BRAND_ORANGE,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.6875rem',
                                                letterSpacing: '0.12em',
                                                textTransform: 'uppercase',
                                            }}>
                                            {position.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Similar Companies */}
            {similarCompanies && similarCompanies.length > 0 && (
                <Card sx={sidebarCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography component="h2" sx={sectionTitleSx}>
                            Similar Companies
                        </Typography>
                        <Box sx={{ spaceY: 2 }}>
                            {similarCompanies.map((similarCompany, index) => (
                                <Box
                                    key={similarCompany._id}
                                    onClick={() => handleGoToSimilarCompany(similarCompany)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                background: similarCompany.profile_picture
                                                    ? 'transparent'
                                                    : getRandomColor(index),
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}>
                                            {similarCompany.profile_picture ? (
                                                <img
                                                    src={similarCompany.profile_picture}
                                                    alt={similarCompany.company_name}
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '4px',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        color: theme[themeStyle].tag_white,
                                                        fontFamily: FONT_ENUM.BOLD_HEADER,
                                                        fontSize: '0.75rem',
                                                        fontWeight: 'bold',
                                                    }}>
                                                    {getCompanyInitial(similarCompany.company_name)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.875rem',
                                                    fontWeight: 'medium',
                                                }}>
                                                {similarCompany.company_name}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                    fontSize: '0.75rem',
                                                }}>
                                                {similarCompany.number_of_jobs} jobs
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        <Star size={12} color={COMPANY_BRAND_ORANGE} fill={COMPANY_BRAND_ORANGE} />
                                        <Typography
                                            sx={{
                                                color: theme[themeStyle].tag_white,
                                                fontFamily: FONT_ENUM.MEDIUM_TEXT,
                                                fontSize: '0.75rem',
                                            }}>
                                            {similarCompany.rating?.toFixed(1) || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Company Gallery */}
            {company?.gallery && company.gallery.length > 0 && (
                <Card sx={sidebarCardSx}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Typography component="h2" sx={sectionTitleSx}>
                            Company Gallery
                        </Typography>
                        <PhotoGrid photos={company.gallery} columns={2} />
                    </CardContent>
                </Card>
            )}

            {companyDetails?.company && (
                <LinkBox
                    link={generateShareLink('company', companyDetails?.company, true)}
                    sx={{
                        backgroundColor: '#1a1a1a',
                        border: '1px solid rgba(255,255,255,0.06)',
                        borderRadius: 2,
                        my: 0,
                    }}
                />
            )}
        </Box>
    );
};

export { CompanySidebar };
